import React, { useState, useEffect, useRef } from "react";
import "./AccoutStats.css";
import EyeIconClose from "../../../assets/images/eye-block.svg";
import EyeIconOpen from "../../../assets/images/eye.svg";
import { ProgressBar } from "react-bootstrap";
import Avg from "../../../assets/images/Dashboard/avg.svg";
import Member from "../../../assets/images/Dashboard/member.svg";
import WinRate from "../../../assets/images/Dashboard/winRate.svg";
import Growth from "../../../assets/images/Dashboard/growth.svg";
import PerformaceYtdImg from "../../../assets/images/Dashboard/performanceYTD.svg";
import BinanceLogo from "../../../assets/images/binance.svg";
import BybitLogo from "../../../assets/images/bybit.svg";
import useApi from "../../../helpers/apiHelper/requestHelper";
import { showBalance } from "../../../store/globalstore";
import { useAtom } from "jotai";
import { getUserTradePermissionType } from "../../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import InfoPopup from "../../sharedPages/CommonComponents/InfoPopup/InfoPopup";

const PERIOD_OPTIONS = [
  { key: "week", label: "Last 7 days" },
  { key: "month", label: "Monthly" },
];

const AccountStats = ({ user, businessListing }) => {
  const api = useApi();

  const [connectedExchange, setConnectedExchange] = useState("Binance");
  const [yourTradingAccount, setYourTradingAccount] = useState(null);
  const [stats, setStats] = useState({});
  const [showBalanceFromStore, setShowBalanceInStore] = useAtom(showBalance);

  const [balanceSinceLastWeek, setBalanceSinceLastWeek] = useState(null);
  const [avgTrade, setAvgTrade] = useState(null);
  const [totalSubscribers, setTotalSubscribers] = useState(0);
  const [winRate, setWinRate] = useState(null);
  const [monthlyGrowth, setMonthlyGrowth] = useState(null);
  const [performanceYTD, setPerformanceYTD] = useState(null);
  const [usersGrowthInMonth, setUsersGrowthInMonth] = useState(0);
  const [growthSinceLastWeek, setGrowthSinceLastWeek] = useState(0);
  const [incomeFromMembers, setIncomeFromMembers] = useState(0);
  const [avgMembership, setAvgMembership] = useState(0);
  const [futuresBalance, setFuturesBalance] = useState(null);
  const [spotBalance, setSpotBalance] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [isPeriodOpen, setIsPeriodOpen] = useState(false);
  const periodDropdownRef = useRef(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const getUserStats = async () => {
    const response = await api("post", "multiCommunity/getSpecificUserStats", {
      userIds: [user?._id],
    });
    let nextStats = response?.status ? response?.data || {} : {};

    // Dedicated spot/futures fields (binanceSpotBalance / bybitSpotBalance)
    // are returned by getCurrentStats — same source VerifyAccountStat uses.
    if (user?._id) {
      const balanceRes = await api(
        "get",
        `multiCommunity/getCurrentStats/${user._id}`
      );
      if (balanceRes?.status && balanceRes?.data) {
        const balanceStats = balanceRes.data;
        nextStats = {
          ...nextStats,
          binanceBalance:
            balanceStats.binanceBalance ?? nextStats.binanceBalance,
          bybitBalance: balanceStats.bybitBalance ?? nextStats.bybitBalance,
          binanceSpotBalance:
            balanceStats.binanceSpotBalance ?? nextStats.binanceSpotBalance,
          bybitSpotBalance:
            balanceStats.bybitSpotBalance ?? nextStats.bybitSpotBalance,
          binanceFuturesBalance:
            balanceStats.binanceFuturesBalance ??
            nextStats.binanceFuturesBalance,
          binanceFutureBalance:
            balanceStats.binanceFutureBalance ?? nextStats.binanceFutureBalance,
          binanceCoinFutureBalance:
            balanceStats.binanceCoinFutureBalance ??
            nextStats.binanceCoinFutureBalance,
          bybitFuturesBalance:
            balanceStats.bybitFuturesBalance ?? nextStats.bybitFuturesBalance,
          bybitFutureBalance:
            balanceStats.bybitFutureBalance ?? nextStats.bybitFutureBalance,
          bybitCoinFutureBalance:
            balanceStats.bybitCoinFutureBalance ??
            nextStats.bybitCoinFutureBalance,
          spotBalance: balanceStats.spotBalance ?? nextStats.spotBalance,
          futuresBalance:
            balanceStats.futuresBalance ?? nextStats.futuresBalance,
          coinFutureBalance:
            balanceStats.coinFutureBalance ?? nextStats.coinFutureBalance,
          balanceSinceLastWeek:
            balanceStats.balanceSinceLastWeek ?? nextStats.balanceSinceLastWeek,
        };
      }
    }

    setStats(nextStats);
  };

  const updateUserBalance = async (exchange) => {
    if (!exchange || !user?._id) return;
    // GET helper ignores the 3rd-arg payload — put params in the URL.
    await api(
      "get",
      `stats/updateUserBalance?exchange=${encodeURIComponent(exchange)}&id=${user._id}`
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const json = await api(
          "get",
          `historyGraph?duration=month&graphType=income&userId=${user?._id}`,
          {
            duration: "month",
            graphType: "income",
            userId: user?._id,
          }
        );

        if (json?.status && Array.isArray(json.data)) {
          setIncomeFromMembers(json?.data[0]?.value);
        }
      } catch (e) {
        console.error("Error fetching chart data:", e);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isPeriodOpen) return;

    const handleClickOutside = (event) => {
      if (
        periodDropdownRef.current &&
        !periodDropdownRef.current.contains(event.target)
      ) {
        setIsPeriodOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isPeriodOpen]);

  useEffect(() => {
    let exchange;
    if (user?.binanceCredentials?.isConnected) {
      exchange = "Binance";
      setConnectedExchange(exchange);
    } else if (user?.bybitCredentials?.isConnected) {
      exchange = "Bybit";
      setConnectedExchange(exchange);
    } else {
      setConnectedExchange("");
    }

    setTotalSubscribers(user?.leaderCommunity?.participants?.length || 0);

    const fetchData = async (connected) => {
      await updateUserBalance(connected);
      await getUserStats();
    };

    if (user) fetchData(exchange);
  }, [user]);

  useEffect(() => {
    if (!stats || Object.keys(stats).length === 0) return;

    const binanceRaw = stats?.binanceBalance;
    const bybitRaw = stats?.bybitBalance;

    const isBinance = Boolean(user?.binanceCredentials?.isConnected);
    const isBybit = Boolean(user?.bybitCredentials?.isConnected);
    const exchangeRaw = isBinance ? binanceRaw : isBybit ? bybitRaw : null;
    const exchangeObj =
      exchangeRaw &&
      typeof exchangeRaw === "object" &&
      !Array.isArray(exchangeRaw)
        ? exchangeRaw
        : null;
    const flatExchangeValue =
      typeof exchangeRaw === "number" || typeof exchangeRaw === "string"
        ? exchangeRaw
        : null;

    const pickNumber = (...values) => {
      for (const value of values) {
        if (value == null || value === "") continue;
        const num = Number(value);
        if (Number.isFinite(num)) return num;
      }
      return 0;
    };

    let spot = 0;
    let futures = 0;
    const permissionType = getUserTradePermissionType(user);

    if (isBinance) {
      spot = pickNumber(
        stats?.binanceSpotBalance,
        exchangeObj?.spotBalance,
        exchangeObj?.balance,
        stats?.spotBalance
      );
      futures = pickNumber(
        exchangeObj?.coinFutureBalance,
        exchangeObj?.futuresBalance,
        exchangeObj?.futureBalance,
        stats?.binanceFuturesBalance,
        stats?.binanceFutureBalance,
        stats?.binanceCoinFutureBalance,
        flatExchangeValue
      );
    } else if (isBybit) {
      spot = pickNumber(
        stats?.bybitSpotBalance,
        exchangeObj?.spotBalance,
        exchangeObj?.balance,
        stats?.spotBalance
      );
      futures = pickNumber(
        exchangeObj?.coinFutureBalance,
        exchangeObj?.futuresBalance,
        exchangeObj?.futureBalance,
        stats?.bybitFuturesBalance,
        stats?.bybitFutureBalance,
        stats?.bybitCoinFutureBalance,
        flatExchangeValue
      );
    }

    const tradingAccountTotal =
      permissionType === "spot"
        ? spot
        : permissionType === "futures"
          ? futures
          : futures + spot;
    setYourTradingAccount(Number(tradingAccountTotal).toFixed(2));
    setFuturesBalance(futures);
    setSpotBalance(spot);

    if (stats?.balanceSinceLastWeek !== undefined) {
      setBalanceSinceLastWeek(
        String(parseFloat(Number(stats?.balanceSinceLastWeek).toFixed(2)))
      );
    } else {
      setBalanceSinceLastWeek(null);
    }
    setAvgTrade(String(parseFloat(Number(stats?.avgTrade || 0).toFixed(2))));
    setWinRate(String(parseFloat(Number(stats?.winRate || 0).toFixed(2))));
    setGrowthSinceLastWeek(
      String(parseFloat(Number(stats?.subFeeSinceWeek || 0).toFixed(2)))
    );
    setUsersGrowthInMonth(
      String(parseFloat(Number(stats?.usersGrowthInMonth || 0).toFixed(2)))
    );
    setAvgMembership(
      String(parseFloat(Number(stats?.avgMembership || 0).toFixed(2)))
    );
    setMonthlyGrowth(
      String(
        parseFloat(Number(stats?.monthlyGrowth?.growth || 0).toFixed(2))
      )
    );
    setPerformanceYTD(
      String(parseFloat(Number(stats?.incomeYTDPercentage || 0).toFixed(2)))
    );
    setDataLoaded(true);
  }, [stats, user]);

  const isLeader = user?.role === "leader";
  const isMemberCard = !businessListing && user?.role !== "leader";
  const permissionType = getUserTradePermissionType(user);
  const showFuturesBalance =
    permissionType === "futures" || permissionType === "both";
  const showSpotBalance =
    permissionType === "spot" || permissionType === "both";
  const isSingleMarketPermission =
    permissionType === "spot" || permissionType === "futures";
  const title = isLeader ? "Total Subscribers" : "Performance YTD";
  const value = isLeader ? totalSubscribers : performanceYTD;
  const Image = isLeader ? Member : PerformaceYtdImg;
  const isConnected = Boolean(connectedExchange);

  const spotLow = showSpotBalance && Number(spotBalance) <= 5;
  const futuresLow = showFuturesBalance && Number(futuresBalance) <= 5;
  const shouldShowLowBalanceAlert =
    dataLoaded &&
    isConnected &&
    showBalanceFromStore &&
    (isSingleMarketPermission
      ? Number(yourTradingAccount) <= 5
      : permissionType === "both"
        ? spotLow || futuresLow
        : false);

  const numberSizeClass = (val) => {
    const len = String(val || "").replace(/[^0-9]/g, "").length;
    if (len >= 6) return " longNumber extraLongNumber";
    if (len >= 5) return " longNumber";
    return "";
  };

  const formatMoney = (val, fractionDigits = 2, minFractionDigits = 0) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return "0";
    return num.toLocaleString(undefined, {
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  const lowBalanceMessage = (() => {
    if (isSingleMarketPermission) {
      return `Your balance is $${formatMoney(yourTradingAccount)}. You might not be able to copy trades from the leader.`;
    }
    if (permissionType === "both") {
      if (spotLow && futuresLow) {
        return `Your spot balance is $${formatMoney(spotBalance)} and futures balance is $${formatMoney(futuresBalance)}. You might not be able to copy trades from the leader.`;
      }
      if (spotLow) {
        return `Your spot balance is $${formatMoney(spotBalance)}. You might not be able to copy trades from the leader.`;
      }
      if (futuresLow) {
        return `Your futures balance is $${formatMoney(futuresBalance)}. You might not be able to copy trades from the leader.`;
      }
    }
    return "";
  })();
  const exchangeLogo =
    connectedExchange === "Bybit"
      ? BybitLogo
      : connectedExchange === "Binance"
      ? BinanceLogo
      : null;
  const weekChange = Number(balanceSinceLastWeek || 0);
  const monthChange = Number(
    stats?.growthSinceLastMonth ?? monthlyGrowth ?? 0
  );
  const periodChange = selectedPeriod === "month" ? monthChange : weekChange;
  const periodChangePositive = periodChange >= 0;
  const selectedPeriodLabel =
    PERIOD_OPTIONS.find((option) => option.key === selectedPeriod)?.label ||
    "Last 7 days";

  const statItems = [
    {
      color: "pink",
      img: Avg,
      title: businessListing ? "Monthly Growth" : "Avg. Trade (ROI%)",
      valueClassName: "numbers d-flex gap-1 align-items-end",
      numericValue: businessListing ? usersGrowthInMonth : avgTrade,
      renderValue: (
        <>
          {businessListing ? usersGrowthInMonth : avgTrade}
          <span className="fs-20 font-medium">%</span>
        </>
      ),
      progressValue: avgTrade,
    },
    {
      color: "blue",
      img: Image,
      title: title,
      valueClassName: "numbers",
      numericValue: value,
      renderValue: <>{value}</>,
      progressValue: value,
    },
    {
      color: "green mb-0",
      img: WinRate,
      title: businessListing ? "Income YTD" : "Win Rate",
      valueClassName: "numbers d-flex gap-1",
      numericValue: businessListing ? performanceYTD : winRate,
      renderValue: (
        <>
          {businessListing ? performanceYTD : winRate}
          <span className="fs-20 font-medium">%</span>
        </>
      ),
      progressValue: winRate,
    },
    {
      color: "orange mb-0",
      img: Growth,
      title: businessListing ? "Avg.Membership" : "Monthly Growth",
      valueClassName: "numbers alphabetText d-flex gap-1",
      numericValue: businessListing ? avgMembership : monthlyGrowth,
      renderValue: businessListing ? (
        <>
          {avgMembership} <span className="fs-20 font-medium">months</span>
        </>
      ) : (
        <>
          {monthlyGrowth}
          <span className="fs-20 font-medium">%</span>%
        </>
      ),
      progressValue: monthlyGrowth,
    },
  ];

  if (isMemberCard) {
    return (
      <div className="accountStatBox accountStatBox-member">
        <div className="memberAccountCard">
          <div className="headingBox">
            <p className="title">Your trading account</p>
            <button
              type="button"
              className="eyeBtn"
              onClick={() => {
                setShowBalanceInStore(!showBalanceFromStore);
              }}
            >
              {showBalanceFromStore ? (
                <img src={EyeIconClose} alt="close Icon" />
              ) : (
                <img src={EyeIconOpen} alt="open Icon" />
              )}
            </button>
          </div>

          <div className="exchangeRow">
            {isConnected ? (
              <>
                <div className="exchangeIdentity">
                  {exchangeLogo && (
                    <img
                      src={exchangeLogo}
                      alt={connectedExchange}
                      className="exchangeLogo"
                    />
                  )}
                </div>
                <span className="connectedBadge">Connected</span>
              </>
            ) : (
              <span className="disconnectedText">You are disconnected</span>
            )}
          </div>

          <div className="memberBalanceRow">
            <p
              className={`numbers${numberSizeClass(
                showBalanceFromStore ? yourTradingAccount : "****"
              )}`}
            >
              {showBalanceFromStore ? (
                <>
                  <span>$</span>{" "}
                  {formatMoney(yourTradingAccount)}
                </>
              ) : (
                "****"
              )}
            </p>
            {shouldShowLowBalanceAlert ? (
              <InfoPopup
                wrapperClassName="memberBalanceAlert"
                buttonClassName="memberBalanceAlert__button"
                popupClassName="memberBalanceAlert__popup"
                customTrigger={
                  <svg
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <defs>
                      <mask id="member-balance-alert-icon-mask">
                        <rect width="24" height="24" fill="#ffffff" />
                        <circle cx="12" cy="8" r="1.15" fill="#000000" />
                        <path
                          d="M12 11.25V16.5"
                          stroke="#000000"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </mask>
                    </defs>
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="currentColor"
                      mask="url(#member-balance-alert-icon-mask)"
                    />
                  </svg>
                }
                contentItems={[
                  {
                    type: "paragraph",
                    text: lowBalanceMessage,
                  },
                ]}
              />
            ) : null}
          </div>

          <div
            className={`memberAccountCard__footer${
              isSingleMarketPermission
                ? " memberAccountCard__footer--singleMarket"
                : ""
            }`}
          >
            <div className="balanceBreakdown">
              {showFuturesBalance && (
                <p>
                  <span className="balanceBreakdown__label">Futures:</span>
                  <span className="balanceBreakdown__amount">
                    $
                    {showBalanceFromStore
                      ? formatMoney(futuresBalance ?? 0)
                      : "****"}
                  </span>
                </p>
              )}
              {showSpotBalance && (
                <p>
                  <span className="balanceBreakdown__label">Spot:</span>
                  <span className="balanceBreakdown__amount">
                    $
                    {showBalanceFromStore
                      ? formatMoney(spotBalance ?? 0)
                      : "****"}
                  </span>
                </p>
              )}
            </div>

            <div className="periodChangeWrap" ref={periodDropdownRef}>
              <button
                type="button"
                className={`weekChange periodChangeBtn ${
                  periodChangePositive ? "positive" : "negative"
                }`}
                aria-expanded={isPeriodOpen}
                aria-haspopup="listbox"
                onClick={() => setIsPeriodOpen((open) => !open)}
              >
                <span className="periodChangeBtn__icon" aria-hidden="true">
                  <span />
                  <span />
                </span>
                <span className="periodChangeBtn__label">
                  {selectedPeriodLabel}:{" "}
                  <span className="periodChangeBtn__value">
                    {periodChangePositive ? "+" : ""}
                    {formatMoney(periodChange)}%
                  </span>
                </span>
              </button>

              {isPeriodOpen && (
                <ul className="periodChangeMenu" role="listbox">
                  {PERIOD_OPTIONS.map((option) => (
                    <li key={option.key}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selectedPeriod === option.key}
                        className={
                          selectedPeriod === option.key ? "is-active" : ""
                        }
                        onClick={() => {
                          setSelectedPeriod(option.key);
                          setIsPeriodOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${
        businessListing ? "businessListingBox" : ""
      } accountStatBox`}
    >
      <div className="leftSide">
        <div className="headingBox">
          <p className="title">
            {businessListing
              ? "Total monthly income"
              : "Your trading account"}
          </p>
          <button
            type="button"
            className="eyeBtn disabled"
            onClick={() => {
              setShowBalanceInStore(!showBalanceFromStore);
            }}
          >
            {showBalanceFromStore ? (
              <img src={EyeIconClose} alt="close Icon" />
            ) : (
              <img src={EyeIconOpen} alt="open Icon" />
            )}
          </button>
        </div>
        <p className="subTitle">
          {businessListing
            ? "Revenue from subscription fees"
            : connectedExchange
            ? `${connectedExchange} Futures USDT`
            : "You are disconnected"}
        </p>
        <p
          className={`numbers${numberSizeClass(
            businessListing ? incomeFromMembers : yourTradingAccount
          )}`}
        >
          {showBalanceFromStore ? (
            <>
              <span>$</span>{" "}
              {businessListing ? incomeFromMembers || 0 : yourTradingAccount}
            </>
          ) : (
            "****"
          )}
        </p>
        <p className="subText">
          <span>
            {businessListing
              ? growthSinceLastWeek || 0
              : balanceSinceLastWeek || 0}
            %
          </span>
          since last week
        </p>
      </div>

      {user?.role === "leader" && (
        <>
          <span className="MiddleDivider"></span>
          <div className="rightSide">
            <div className="row">
              {statItems.map((item, index) => (
                <div key={index} className="col-lg-6 col-md-6 col-6">
                  <div className={`statProgressItem ${item.color}`}>
                    <div className="title">
                      <img src={item.img} alt="" />
                      <p>{item.title}</p>
                    </div>
                    <p
                      className={`${item.valueClassName}${numberSizeClass(
                        item.numericValue
                      )}`}
                    >
                      {item.renderValue}
                    </p>
                    {!businessListing && (
                      <ProgressBar now={item.progressValue} />
                    )}
                  </div>
                </div>
              ))}
            </div>
            {businessListing && (
              <span className="d-sm-none d-block MiddleDivider"></span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountStats;
